import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { gamesService, type Game } from '@/services/games.service';

export default function PlayHubPage() {
  const navigate = useNavigate();
  const [searchTerm] = useState('');
  const [games, setGames] = useState<Game[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load games on mount
  useEffect(() => {
    const loadGames = async () => {
      try {
        const gamesData = await gamesService.getAllGames();
        setGames(gamesData.filter(g => g.is_active));
      } catch (error) {
        console.error('Erro ao carregar games:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadGames();
  }, []);

  // Filter games based on search term
  const filteredGames = games.filter(game => 
    game.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    game.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    game.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handlePlayGame = async (game: Game) => {
    try {
      // Start game session
      const session = await gamesService.startGameSession(game.id);
      console.log('Game session started:', session);
      
      // Navigate based on game slug
      navigate(`/play/${game.slug}`);
    } catch (error) {
      console.error('Erro ao iniciar game:', error);
    }
  };

  return (
      <div className="min-h-screen bg-surface-primary text-content-primary">
        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Page Heading */}
        <div className="flex flex-col md:flex-row justify-between items-end gap-6 mb-10">
          <div className="flex flex-col gap-2 max-w-2xl">
            <div className="flex items-center gap-2 text-accent mb-1">
              <span className="material-symbols-outlined text-sm">bolt</span>
              <span className="text-xs font-bold uppercase tracking-widest">MVP Alpha Access</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black leading-tight tracking-tight text-content-primary">
              Hub Play: <span className="text-accent">Escolha seu Desafio</span>
            </h1>
            <p className="text-content-muted text-lg">
              Participe dos desafios interativos, teste suas habilidades em IA e ganhe XP para subir
              no ranking global.
            </p>
          </div>
          <div className="hidden lg:block pb-2">
            <div className="bg-accent/10 border border-accent/20 p-4 rounded-xl flex items-center gap-4">
              <div className="size-12 rounded-lg bg-accent flex items-center justify-center shadow-lg shadow-accent/30">
                <span className="material-symbols-outlined text-white text-3xl">military_tech</span>
              </div>
              <div>
                <p className="text-xs font-bold text-content-muted uppercase tracking-tighter">
                  Temporada 01
                </p>
                <p className="text-sm font-bold">14 Dias Restantes</p>
              </div>
            </div>
          </div>
        </div>

        {/* Section Header: Active Games */}
        <div className="flex items-center gap-3 mb-6">
          <h2 className="text-xl font-bold px-1">Desafios Disponíveis</h2>
          {searchTerm && (
            <span className="text-sm text-slate-400">
              ({filteredGames.length} resultado{filteredGames.length !== 1 ? 's' : ''})
            </span>
          )}
          <div className="h-px flex-1 bg-gradient-to-r from-white/10 to-transparent" />
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
          </div>
        ) : filteredGames.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 mb-16">
            {filteredGames.map((game) => (
            <div
              key={game.id}
              className="group relative flex flex-col gap-4 p-5 rounded-2xl bg-white/5 backdrop-blur-md border border-white/10 shadow-lg shadow-accent/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-accent/20"
            >
              <div
                className="relative w-full aspect-[21/9] bg-center bg-no-repeat bg-cover rounded-xl overflow-hidden"
                style={{ backgroundImage: `url("${game.thumbnail}")` }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute top-4 left-4 flex gap-2">
                  <span className="bg-black/50 backdrop-blur-md px-3 py-1 rounded-full text-2xs font-bold text-white uppercase tracking-wider border border-white/20">
                    {game.category}
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <h3 className="text-2xl font-bold text-content-primary group-hover:text-accent transition-colors">
                    {game.name}
                  </h3>
                  <p className="text-content-muted text-sm leading-relaxed">{game.short_description}</p>
                </div>
              </div>
              <div className="mt-auto pt-4 flex items-center justify-between border-t border-white/10">
                <div className="flex items-center gap-2 text-accent">
                  <span className="material-symbols-outlined text-lg">star</span>
                  <span className="text-sm font-bold uppercase tracking-wide">
                    +{game.xp_reward} XP
                  </span>
                </div>
                <button
                  onClick={() => handlePlayGame(game)}
                  className="bg-accent hover:bg-accent-hover text-accent-contrast px-8 py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-accent/20 transition-all active:scale-95 flex items-center gap-2"
                >
                  Jogar Agora
                  <span className="material-symbols-outlined text-sm">play_arrow</span>
                </button>
              </div>
            </div>
          ))}
        </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 mb-16">
            <span className="material-symbols-outlined text-6xl text-slate-600 mb-4">search_off</span>
            <h3 className="text-xl font-bold text-content-primary mb-2">Nenhum desafio encontrado</h3>
            <p className="text-slate-400 text-sm">Tente buscar por outro termo</p>
          </div>
        )}
      </main>
      </div>
  );
}
